import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { api, fetchUserProfile, UserProfile } from '../services/api';
import { useCompleteSignIn } from '../hooks/useCompleteSignIn';

interface Props {
  onProfileUpdate: (profile: UserProfile) => void;
  onSuccess?: () => void;
  onPassword?: () => void;
  magicToken?: string;
  initialEmail?: string;
  existingAccount?: boolean;
}

export const MagicEmailAuth = ({ onProfileUpdate, onSuccess, onPassword, magicToken, initialEmail = '', existingAccount = false }: Props) => {
  const [email, setEmail] = useState(initialEmail);
  const [confirming, setConfirming] = useState(!!magicToken);
  const [sent, setSent] = useState(false);
  const [welcome, setWelcome] = useState<'new' | 'returning' | null>(null);
  const [needsName, setNeedsName] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inFlight = useRef(false);
  const id = useId();
  const complete = useCompleteSignIn(onProfileUpdate);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current || (!confirming && cooldown > 0)) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      if (confirming) {
        const response = await api.post('/auth/email/magic/confirm/', { token: magicToken });
        localStorage.removeItem('googleFirstName');
        await complete(response.data.key, 'email', response.data.is_new_account === true);
        setNeedsName(!!response.data.needs_name);
        setWelcome(response.data.is_new_account ? 'new' : 'returning');
      } else {
        await api.post('/auth/email/magic/', { email: email.trim() });
        setSent(true); setCooldown(60);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        const retry = Number(err.response.headers?.['retry-after']);
        setCooldown(Number.isFinite(retry) && retry > 0 ? Math.ceil(retry) : 60);
        setError('Please wait before requesting another link.');
      } else if (confirming && err.response?.status === 400) {
        setError('This link has expired or already been used. Request a new link below.');
      } else {
        setError(err.response?.data?.detail || 'We couldn’t complete that request. Please try again.');
      }
    } finally { inFlight.current = false; setBusy(false); }
  };
  const saveName = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    inFlight.current = true; setBusy(true); setError('');
    try {
      await api.post('/auth/email/name/', { first_name: firstName.trim(), last_name: lastName.trim() });
      onProfileUpdate(await fetchUserProfile());
      setNeedsName(false);
      onSuccess?.();
    } catch {
      setError('We couldn’t save your name. Please try again.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  if (welcome) return <div className="space-y-6">
    <div className="relative overflow-hidden rounded-xl bg-brand-700 px-6 py-8 text-white">
      <Sparkles aria-hidden="true" className="absolute right-5 top-5 h-8 w-8 text-amber-200" />
      <span className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15"><Check aria-hidden="true" className="h-6 w-6" /></span>
      <p className="mb-3 text-xs uppercase tracking-widest text-white/80">{welcome === 'new' ? 'Your account is ready' : 'You’re signed in'}</p>
      <h2 className="font-display text-3xl leading-tight tracking-tight">{welcome === 'new' ? 'A seat at the table. Just for you.' : 'Welcome back. Let’s make plans.'}</h2>
    </div>
    {needsName ? <form onSubmit={saveName} aria-label="Your name" className="space-y-4">
      <p className="text-sm leading-relaxed text-ink-600">One last thing — what should we call you?</p>
      <label htmlFor={`${id}-first`} className="block text-sm text-ink-700">First name
        <input id={`${id}-first`} autoComplete="given-name" required maxLength={150} value={firstName} onChange={e => setFirstName(e.target.value)} className="mt-1 w-full rounded-md border border-ink-200 bg-surface px-3 py-2 text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </label>
      <label htmlFor={`${id}-last`} className="block text-sm text-ink-700">Last name (optional)
        <input id={`${id}-last`} autoComplete="family-name" maxLength={150} value={lastName} onChange={e => setLastName(e.target.value)} className="mt-1 w-full rounded-md border border-ink-200 bg-surface px-3 py-2 text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </label>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-3 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">{busy ? 'Saving…' : 'Find your next table'} <ArrowRight aria-hidden="true" className="h-4 w-4" /></button>
    </form> : <>
      <p className="text-sm leading-relaxed text-ink-600">{welcome === 'new' ? 'Welcome to Eatsy. Discover somewhere special, save your favourites and make your next meal one to remember.' : 'Your favourites, your dining memories, and a whole city of possibilities. Where shall we go next?'}</p>
      <button onClick={onSuccess} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-3 text-sm font-medium text-white hover:bg-brand-700">Find your next table <ArrowRight aria-hidden="true" className="h-4 w-4" /></button>
    </>}
  </div>;

  return <div className="space-y-5">
    <h2 className="font-display text-2xl tracking-tight text-ink-900">{confirming ? 'Welcome to Eatsy' : sent ? 'Check your email' : existingAccount ? 'You already have an Eatsy account' : 'Sign up or sign in'}</h2>
    <p className="text-sm leading-relaxed text-ink-600">{confirming ? 'Continue to create your Eatsy account or sign back in if you already have one.' : sent ? `We sent a sign-in link to ${email}. It works once and expires in 15 minutes. Use the newest email if you request another.` : existingAccount ? 'Would you like a link to sign in without a password? We’ll send it to the email below.' : 'New to Eatsy? Your email link creates your account. Already a member? The same link signs you back in. No password needed.'}</p>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <form onSubmit={submit} aria-label="Email sign-in" className="space-y-4">
      {!confirming && !sent && <label htmlFor={id} className="block text-sm text-ink-700">Email
        <input id={id} type="email" autoComplete="email" required maxLength={254} value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-ink-200 bg-surface px-3 py-2 text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </label>}
      <button disabled={busy || (!confirming && cooldown > 0)} className="w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">{busy ? 'Please wait…' : confirming ? 'Continue to Eatsy' : cooldown > 0 ? `Resend in ${cooldown}s` : sent ? 'Resend sign-in link' : existingAccount ? 'Email me a sign-in link' : 'Send sign-in link'}</button>
    </form>
    {sent && <p role="status" className="text-sm text-ink-600">Check your spam folder too.</p>}
    {sent && process.env.REACT_APP_ENVIRONMENT === 'staging' && <a href="/_staging/inbox/" target="_blank" rel="noreferrer" className="block text-center text-sm text-brand-700 underline">Open test emails</a>}
    {confirming && error && <button onClick={() => { setConfirming(false); setSent(false); setError(''); }} className="w-full text-sm text-brand-700 hover:underline">Request a new sign-in link</button>}
    {!confirming && sent && <button onClick={() => { setSent(false); setError(''); }} className="w-full text-sm text-brand-700 hover:underline">Use a different email</button>}
    {onPassword && !confirming && <button onClick={onPassword} className="w-full text-sm text-ink-600 hover:underline">Sign in with a password</button>}
  </div>;
};
